import type { Day } from '../core/calendar.ts';
import { add, round2, ZERO, type Decimal } from '../core/decimal.ts';
import { bucketIndexContaining, timeBuckets } from '../forecast/window.ts';
import { frequencyStep, scheduleDate } from '../models/frequency.ts';
import { paidOn } from '../models/transaction.ts';
import type { Series, Transaction, Window } from '../types/domain.ts';

/**
 * Dates a transaction pays on before `windowEnd`, as PaymentCalculator schedules them: oneTime
 * on its start date; recurring anchored at the start date (R6), stopping after `endDate` or
 * `endAfterOccurrences` occurrences (counted from the start, including those before the
 * window). A non-positive custom interval schedules nothing.
 */
export function scheduledDates(transaction: Transaction, windowEnd: Day): readonly Day[] {
  const step = frequencyStep(transaction.frequency);
  if (step === null) return transaction.startDate < windowEnd ? [transaction.startDate] : [];
  if (step.count <= 0) return [];
  const limit = transaction.endAfterOccurrences ?? Number.POSITIVE_INFINITY;
  const dates: Day[] = [];
  for (let date = transaction.startDate; dates.length < limit && date < windowEnd; date = scheduleDate(transaction.startDate, step, dates.length)) {
    if (transaction.endDate !== null && date > transaction.endDate) break;
    dates.push(date);
  }
  return dates;
}

/** What an occurrence pays: by default what was paid on that date (R27), else the R11 amount. */
export type OccurrenceAmount = (transaction: Transaction, date: Day) => Decimal;

/**
 * PaymentCalculator.calculate: sum each active transaction's payments, at their R11 amounts, into the
 * bucket whose [start, end) contains it (R7), then round each bucket to 2dp. Occurrences
 * `include` rejects are left out. Amounts are added in transaction order, then date order
 * (Decimal addition truncates, so order matters).
 */
export function calculatePayments(
  transactions: readonly Transaction[],
  window: Window,
  include: (date: Day) => boolean = () => true,
  amount: OccurrenceAmount = paidOn,
): Series {
  const buckets = timeBuckets(window);
  const totals: Decimal[] = buckets.map(() => ZERO);
  for (const transaction of transactions) {
    if (!transaction.isActive) continue;
    for (const date of scheduledDates(transaction, window.end)) {
      const index = bucketIndexContaining(buckets, date);
      if (index !== null && include(date)) totals[index] = add(totals[index]!, amount(transaction, date));
    }
  }
  return buckets.map((bucket, i) => ({ ...bucket, amount: round2(totals[i]!) }));
}
